import { ApiError, fetchWithSession, resolveBaseUrl } from '@/api/client';
import type { components } from '@/api/schema';

/** A technician's own day, in driving order. */
export type FieldDay = components['schemas']['FieldDay'];
/** One stop on that day. */
export type FieldStop = components['schemas']['FieldStop'];
/** Everything needed to run one visit. */
export type FieldJob = components['schemas']['FieldJob'];
/** A file stored against the visit. */
export type FieldAttachment = components['schemas']['FieldAttachment'];
/** A line on the visit's checklist. */
export type FieldChecklistItem = components['schemas']['FieldChecklistItem'];
/** A note written on the visit. */
export type FieldNote = components['schemas']['FieldNote'];
/** A part the visit expects to consume. */
export type FieldMaterial = components['schemas']['FieldMaterial'];
/** A unit installed at the address. */
export type FieldEquipment = components['schemas']['FieldEquipment'];
/** The customer's acceptance of the work. */
export type FieldSignature = components['schemas']['FieldSignature'];
/** The answer to any queued mutation. */
export type FieldMutationResult = components['schemas']['FieldMutationResult'];
/** The answer to a queued replacement handoff. */
export type FieldHandoffResult = components['schemas']['FieldHandoffResult'];
/** Where a visit has got to. */
export type FieldJobStatus = FieldJob['status'];

/** What the office holds, returned with a refusal the handset lost. */
export interface FieldConflict {
  readonly jobId: string;
  readonly currentStatus: FieldJobStatus;
  readonly currentVersion: number;
  readonly detail: string;
}

/**
 * Thrown when a queued mutation arrived after the office had already moved on.
 *
 * Carries the server's own state rather than only a status code, because that is the whole
 * difference between a message a technician can act on ("dispatch put this job on hold") and one
 * they cannot ("409").
 */
export class FieldConflictError extends ApiError {
  readonly current: FieldConflict;

  constructor(current: FieldConflict) {
    super(409, current.detail);
    this.name = 'FieldConflictError';
    this.current = current;
  }
}

function conflictFrom(status: number, payload: unknown): FieldConflictError | null {
  if (status !== 409 || payload === null || typeof payload !== 'object') {
    return null;
  }
  const body = payload as Record<string, unknown>;
  if (typeof body.currentStatus !== 'string' || typeof body.jobId !== 'string') {
    return null;
  }
  return new FieldConflictError({
    jobId: body.jobId,
    currentStatus: body.currentStatus as unknown as FieldJobStatus,
    currentVersion: typeof body.currentVersion === 'number' ? body.currentVersion : 0,
    detail:
      typeof body.detail === 'string' && body.detail.trim() !== ''
        ? body.detail
        : 'The office changed this job while you were offline.',
  });
}

/**
 * Sends one request to the field API and turns a refusal into something a screen can say.
 *
 * Deliberately not the generated typed client. Every mutation here may also be replayed later by
 * the outbox, unattended and out of any React tree, so the transport has to be a plain function
 * over a path and a body — the same one the queue uses when it drains at a kerb.
 *
 * @param path the path below /api/field
 * @param method the HTTP method
 * @param body the request body, when there is one
 * @returns the parsed response
 */
export async function fieldRequest<T>(path: string, method = 'GET', body?: unknown): Promise<T> {
  const response = await fetchWithSession(
    new Request(resolveBaseUrl() + '/api/field' + path, {
      method,
      ...(body === undefined
        ? {}
        : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
    }),
  );

  if (!response.ok) {
    const payload: unknown = await response.json().catch(() => null);
    const conflict = conflictFrom(response.status, payload);
    if (conflict !== null) {
      throw conflict;
    }
    const detail =
      payload !== null && typeof payload === 'object' && 'detail' in payload
        ? payload.detail
        : undefined;
    throw new ApiError(
      response.status,
      typeof detail === 'string' && detail.trim() !== '' ? detail : 'That could not be saved.',
    );
  }
  return (await response.json()) as T;
}

/**
 * Reads the signed-in technician's own route.
 *
 * @param date the day, as YYYY-MM-DD in the workspace's zone; omitted means today
 * @returns the day, in driving order
 */
export function readDay(date?: string): Promise<FieldDay> {
  return fieldRequest<FieldDay>(
    date === undefined ? '/route' : '/route?date=' + encodeURIComponent(date),
  );
}

/**
 * Reads one visit, whole.
 *
 * @param jobId the visit
 * @returns everything needed to run it
 */
export function readJob(jobId: string): Promise<FieldJob> {
  return fieldRequest<FieldJob>('/jobs/' + encodeURIComponent(jobId));
}

function asDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('That photograph could not be read.'));
      }
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error('That photograph could not be read.'));
    };
    reader.readAsDataURL(blob);
  });
}

/**
 * Reads a stored photograph for a thumbnail.
 *
 * Fetched with the session and handed back as a data URL: the download route wants the bearer
 * token, which an image tag cannot send.
 *
 * @param jobId the visit
 * @param attachmentId the stored file
 * @returns the image as a data URL
 */
export async function readAttachment(jobId: string, attachmentId: string): Promise<string> {
  const response = await fetchWithSession(
    new Request(
      resolveBaseUrl() +
        '/api/jobs/' +
        encodeURIComponent(jobId) +
        '/attachments/' +
        encodeURIComponent(attachmentId),
    ),
  );
  if (!response.ok) {
    throw new ApiError(response.status, 'That photograph could not be loaded.');
  }
  return asDataUrl(await response.blob());
}

/**
 * What a visit can become next, worked out on the handset.
 *
 * A copy of the server's transition graph, and the only copy of a server rule this stage keeps. It
 * is here because of what field mode is for: with no signal there is no re-read, so a screen that
 * took the next set of buttons from the server's last answer would offer none at all the moment a
 * technician pressed the first one, and the visit would be stuck at "on my way" until the van found
 * a bar of signal. Guessing is the right behaviour; the server still decides.
 *
 * Kept honest by a test that walks every status and compares this against the graph the API
 * publishes, so a lifecycle changed in Java and not here fails the build rather than a visit.
 *
 * @param status where the visit is now
 * @returns what it may become, in the lifecycle's own order
 */
export function allowedAfter(status: FieldJobStatus): FieldJobStatus[] {
  switch (status) {
    case 'SCHEDULED':
      return ['DISPATCHED', 'ON_HOLD', 'CANCELLED'];
    case 'DISPATCHED':
      return ['EN_ROUTE', 'ON_HOLD', 'CANCELLED'];
    case 'EN_ROUTE':
      return ['ON_SITE', 'ON_HOLD', 'CANCELLED'];
    case 'ON_SITE':
      return ['COMPLETED', 'ON_HOLD', 'CANCELLED'];
    case 'ON_HOLD':
      return ['SCHEDULED', 'CANCELLED'];
    default:
      // COMPLETED and CANCELLED are terminal. A visit does not reopen from a van.
      return [];
  }
}

/**
 * The words a technician reads on a status button.
 *
 * @param status the status being moved to
 * @returns the button's label
 */
export function statusAction(status: FieldJobStatus): string {
  switch (status) {
    case 'EN_ROUTE':
      return 'On my way';
    case 'ON_SITE':
      return 'Arrived';
    case 'COMPLETED':
      return 'Finish the visit';
    case 'ON_HOLD':
      return 'Put on hold';
    case 'CANCELLED':
      return 'Cancel the visit';
    case 'DISPATCHED':
      return 'Dispatch';
    default:
      return 'Schedule';
  }
}

/**
 * Reads a clock time out of an instant, in the workspace's zone.
 *
 * @param instant the ISO instant
 * @param timeZone the IANA zone the workspace works in
 * @returns the time of day, without the date
 */
export function clock(instant: string, timeZone: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  }).format(new Date(instant));
}
